import React from 'react';

import { IAudienceSegment } from '~/src/dash/api/console-api';
import { ICustomFilterConfig } from '~/src/dash/components/filters/common';
import {
  ICustomRenderCellJSXProps,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  DateTimeRender,
  EllipsisWithTooltipRender,
} from '~/src/dash/components/tables/ListViewTable/ListViewTableRow/templates';
import { Text } from '~/src/dash/publicaUI';
import { IMenuList } from '~/src/dash/types/menuList';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { PARTNERS } from '~/src/dash/views/segments/audience/common';

const formatActiveStatusValue = (value: string) => {
  return value === '2';
};

const formatPartners = (value: string) => {
  return value === '' ? 'First Party' : value;
};

const getReachValue = (value: number) => {
  switch (value) {
    case -1:
      return 'Error';
    case -2:
      return 'N/A';
    case -3:
      return 'Pending';
    default:
      return convertLargeNumbersToRoundedString(value);
  }
};

const AbbreviatedReachRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const renderedValue = getReachValue(value as number);
  return (
    <Text small color={(value as number) < 0 ? publicaColors.mediumGray : 'inherit'}>
      {renderedValue}
    </Text>
  );
};

export const formatSegmentsResponseIntoLVTRows = (
  segments: IAudienceSegment[],
  cfg: IAudienceManagerListViewTable,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  segments.forEach((segment) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: string | number | boolean = segment[responseKey as keyof IAudienceSegment];
      if (isNumeric) {
        value = Number(value);
      }
      if (formatValue) {
        value = formatValue(value);
      }

      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

type SegmentsAPIKey =
  | 'ID'
  | 'UUID'
  | 'Active'
  | 'Name'
  | 'URL'
  | 'IsFirstParty'
  | 'ThirdPartySegmentIDs'
  | 'Logic'
  | 'LogicQuery'
  | 'LogicQueryJson'
  | 'AudienceSegmentType'
  | 'LiverampOTTHubPrefix'
  | 'LiverampCPM'
  | 'DataProvider'
  | 'Partner'
  | 'CadentFilePath'
  | 'CadentMeta'
  | 'UpdatedAt'
  | 'CreatedAt'
  | 'DataSize'
  | 'UniqueUserCount';

// more to be added next below
interface IAudienceManagerListViewTable {
  // IMPORTANT: PLEASE READ COMMENTS BELOW
  // add mandatory columns 'ID', 'Name'
  // if enabling active switch, 'Status' is mandatory
  // if enabling actions, 'Actions' is mandatory
  // add ALL needed metrics for display AND for sorting
  // if metric is only used for sorting, add `isHidden: true`
  columns: IListViewTableColumns;
}

// do not use this interface for other configs.
// make sure to create your own in order to specify your API Keys types
export interface IAudienceManagerListViewConfig {
  cypressClassName: string; // use main view component name
  defaultState: {
    activeColumnKeys?: SegmentsAPIKey[];
    isTableExpanded?: boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage?: RowsPerPage;
  };
  columnSelector: {
    resetKeys?: SegmentsAPIKey[]; // what keys should be made active on reset
  };
  filters: ICustomFilterConfig;
  isInitialized?: boolean;
  sorts: SegmentsAPIKey[];
  table: IAudienceManagerListViewTable;
}

export const audienceManagerListViewConfig = {
  cypressClassName: 'audiencemanagersettingslistview',
  defaultState: {
    activeColumnKeys: ['DataSize', 'UniqueUserCount', 'Partner', 'CreatedAt', 'UpdatedAt'],
    isTableExpanded: false,
    order: 'desc',
    orderBy: 'DataSize',
    page: 0,
    rowsPerPage: 10,
  },
  columnSelector: {
    resetKeys: ['DataSize', 'UniqueUserCount', 'Partner', 'CreatedAt', 'UpdatedAt'],
  },
  filters: {
    Partners: {
      formatProps: (list: IMenuList) => {
        const result: string[] = [];
        Object.values(list).forEach((item) => {
          result.push(PARTNERS[item.ID].value);
        });
        return result;
      },
      filterFn: (item: IAudienceSegment, selectedIDs: string[]) => {
        return selectedIDs.includes(item.Partner);
      },
    },
    Status: {
      formatProps: (list: IMenuList) => {
        return Object.values(list)[0].title === 'Active';
      },
      filterFn: (item: IAudienceSegment, isActiveOnly: boolean) => {
        if (isActiveOnly) {
          return item.Active === '2';
        }
        return item.Active !== '2';
      },
      defaultSelected: ['1'], // save this as an example
    },
  },
  sorts: ['CreatedAt', 'UpdatedAt'],
  table: {
    columns: {
      ID: {
        isHidden: true,
        isSearchable: true,
        key: 'ID',
        label: 'ID',
        requestKey: '',
        responseKey: 'ID',
        valueType: 'string',
      },
      // mandatory for active switch column
      Status: {
        defaultOrderDirection: 'desc',
        formatValue: formatActiveStatusValue,
        headerRenderType: 'switch',
        key: 'Status',
        label: 'Status',
        requestKey: '',
        responseKey: 'Active',
        valueType: 'boolean',
      },
      Name: {
        cellJSX: EllipsisWithTooltipRender,
        cellRenderType: 'name_link',
        defaultOrderDirection: 'asc',
        isSearchable: true,
        key: 'Name',
        label: 'Segment',
        headerRenderType: 'name',
        requestKey: '',
        responseKey: 'Name',
        valueType: 'string',
      },
      Actions: {
        disableSort: true,
        cellRenderType: 'actions',
        headerRenderType: 'actions',
        key: 'Actions',
        label: 'Actions',
        requestKey: '',
        responseKey: '',
        valueType: 'component',
      },
      DataSize: {
        cellJSX: AbbreviatedReachRender,
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        isDefaultActive: true,
        isNumeric: true,
        key: 'DataSize',
        label: 'Total Audience Reach',
        requestKey: '',
        responseKey: 'DataSize',
        valueType: 'number',
      },
      UniqueUserCount: {
        cellJSX: AbbreviatedReachRender,
        defaultOrderDirection: 'desc',
        headerRenderType: 'number',
        isDefaultActive: true,
        isNumeric: true,
        key: 'UniqueUserCount',
        label: 'Total Publisher Reach',
        requestKey: '',
        responseKey: 'UniqueUserCount',
        valueType: 'number',
      },
      DataProvider: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        headerRenderType: 'string',
        isSearchable: true,
        key: 'DataProvider',
        label: 'Data Provider',
        requestKey: '',
        responseKey: 'DataProvider',
        valueType: 'string',
      },
      Partner: {
        cellRenderType: 'string',
        defaultOrderDirection: 'asc',
        formatValue: formatPartners,
        headerRenderType: 'string',
        isDefaultActive: true,
        isSearchable: true,
        key: 'Partner',
        label: 'Partner',
        requestKey: '',
        responseKey: 'Partner',
        valueType: 'string',
      },
      CreatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'CreatedAt',
        label: 'Created Date',
        requestKey: '',
        responseKey: 'CreatedAt',
        sortLabel: 'Last Created',
        valueType: 'date',
      },
      UpdatedAt: {
        cellJSX: DateTimeRender,
        cellRenderType: 'date',
        defaultOrderDirection: 'desc',
        headerRenderType: 'date',
        isDate: true,
        key: 'UpdatedAt',
        label: 'Last Updated',
        requestKey: '',
        responseKey: 'UpdatedAt',
        sortLabel: 'Last Updated',
        valueType: 'date',
      },
    },
  },
} as IAudienceManagerListViewConfig;
